import React, { useState, useEffect, useMemo } from 'react';
import { Typography, Button, Grid2, Card, CardContent, TextField, MenuItem, Box, LinearProgress, IconButton, Tooltip, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, Select, useTheme, Divider } from '@mui/material';
import { Add, Delete, Edit, CheckCircle, Done, Assignment, Person, Schedule, Flag, ExpandMore } from '@mui/icons-material';
import { getEventTasks, createTask, updateTask, deleteTask, getTask } from '../actions/tasks'
import { useNavigate, useParams } from 'react-router-dom';
import AccessDenied from '../components/AccessDenied';
import { useAlert } from '../contexts/AlertContext';
import { cardGridSx, cardItemSx } from '../utils/cardLayout';

const TaskManagement = () => {
  const theme = useTheme();
  const { showAlert } = useAlert();
  const { eventId } = useParams();
  const [tasks, setTasks] = useState([]);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    priority: 'Low',
    status: 'To Do',
    assignee: '',
    duedate: '',
    notes: ''
  });
  const [editingTask, setEditingTask] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [openTaskDialog, setOpenTaskDialog] = useState(false);
  const [expandedTexts, setExpandedTexts] = useState({});
  const [accessDenied, setAccessDenied] = useState(false);

  // Filter states
  const [priorityFilter, setPriorityFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState('');

  // Fetch tasks on load
  useEffect(() => {
    const fetchTasks = async () => {
      try {
        const fetchedTasks = await getEventTasks(eventId);
        setTasks(fetchedTasks);
      } catch (error) {
        if (error?.response?.status === 403) {
          setAccessDenied(true);
        } else {
          showAlert(error?.response?.data?.error || error?.response?.data?.message || error.message || 'Failed to load data', 'error');
        }
      }
    };
    fetchTasks();
  }, [eventId]);

  const capitalizeName = (name) => {
    if (!name) return '';
    return name
      .split(' ') 
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const getUniqueFilterValues = (key) => {
    const values = tasks.map(task => {
      let value = task[key];
      
      if (!value || value.trim() === '') {
        return key === 'assignee' ? 'Unassigned' : 'Uncategorized';
      }
      return value;
    });

    const uniqueValues = Array.from(new Set(
      values.map(value => value.toLowerCase())
    )).map(value => {
      if (value === 'unassigned' || value === 'uncategorized') {
        return value.charAt(0).toUpperCase() + value.slice(1);
      }
      return capitalizeName(value);
    }).sort();

    return uniqueValues;
  };

  // Handle filtering of tasks
  const filteredTasks = useMemo(() => {
    return tasks.filter(task => {
      const priorityValue = task.priority || '';
      const statusValue = task.status || '';
      const assigneeValue = task.assignee || '';

      const priorityMatch = !priorityFilter ||
        (priorityFilter === 'Uncategorized' && priorityValue.trim() === '') ||
        (priorityValue === priorityFilter);

      const statusMatch = !statusFilter ||
        (statusFilter === 'Uncategorized' && statusValue.trim() === '') ||
        (statusValue === statusFilter);

      const assigneeMatch = !assigneeFilter ||
        (assigneeFilter === 'Unassigned' && assigneeValue.trim() === '') ||
        (capitalizeName(assigneeValue) === assigneeFilter);

      return priorityMatch && statusMatch && assigneeMatch;
    });
  }, [tasks, priorityFilter, statusFilter, assigneeFilter]);

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewTask({ ...newTask, [name]: value });
  };

  // Handle adding or editing a task
  const handleAddOrEditTask = async () => {
    try {
      if (editingTask) {
        await updateTask(editingTask.id, newTask);
      } else {
        await createTask(eventId, newTask);
      }
      
      const fetchedTasks = await getEventTasks(eventId);
      setTasks(fetchedTasks);
  
      setNewTask({ title: '', description: '', priority: 'Low', status: 'To Do', assignee: '', duedate: '', notes: '' });
      setOpenTaskDialog(false);
      setEditingTask(null);
    } catch (error) {
      showAlert(error?.response?.data?.error || error?.response?.data?.message || error.message || 'Operation failed', 'error');
      console.error('Failed to save task', error);
    }
  };

  // Handle task editing
  const handleEdit = (task) => {
    setNewTask({
      title: task.title,
      description: task.description,
      priority: task.priority,
      status: task.status,
      assignee: task.assignee,
      duedate: task.duedate,
      notes: task.notes,
    });
    setEditingTask(task);
    setOpenTaskDialog(true);
  };

  // Handle opening delete confirmation
  const handleDeleteOpen = (id) => {
    setTaskToDelete(id);
    setOpenDeleteDialog(true);
  };

  // Confirm deletion of a task
  const handleDeleteConfirm = async () => {
    try {
      await deleteTask(taskToDelete);

      const fetchedTasks = await getEventTasks(eventId);
      setTasks(fetchedTasks);

      setOpenDeleteDialog(false);
      setTaskToDelete(null);
    } catch (error) {
      showAlert(error?.response?.data?.error || error?.response?.data?.message || error.message || 'Operation failed', 'error');
      console.error('Failed to delete task', error);
    }
  };

  const handleDeleteCancel = () => {
    setOpenDeleteDialog(false);
    setTaskToDelete(null);
  };

  const handleTextExpand = (taskId, field) => {
    const key = `${taskId}-${field}`;
    setExpandedTexts(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Mark task as complete
  const handleComplete  = async (id) => {
    const task = await getTask(id);
    await updateTask(id, { ...task, status: 'Done' });
    const fetchedTasks = await getEventTasks(eventId);
    setTasks(fetchedTasks);
  };

  // Progress and UI helper functions
  const calculateProgress = (status) => {
    const progressMap = {
      'To Do': 0,
      Scheduled: 25,
      'In Progress': 50,
      'Pending Review': 75,
      Done: 100,
      Cancelled: 100,
      'On Hold': 100,
      Blocked: 100,
    };
    return progressMap[status] || 0;
  };

  const getProgressBarColor = (status) => {
    const colorMap = {
      Done: theme.palette.success.main,
      Cancelled: theme.palette.error.main,
      'On Hold': theme.palette.warning.main,
      Blocked: theme.palette.warning.main,
      default: theme.palette.primary.main,
    };
    return colorMap[status] || colorMap.default;
  };

  const getCardBorderColor = (status) => {
    const borderColorMap = {
      Done: '2px solid ' + theme.palette.success.main,
      Cancelled: '2px solid ' + theme.palette.error.main,
      'On Hold': '2px solid ' + theme.palette.warning.main,
      Blocked: '2px solid ' + theme.palette.warning.main,
    };
    return borderColorMap[status] || (theme.palette.mode === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.1)'
      : '1px solid rgba(255, 255, 255, 0.2)');
  };

  const getPriorityColor = (priority) => {
    const colorMap = {
      High: theme.palette.error.main,
      Medium: theme.palette.warning.main,
      Low: theme.palette.success.main,
    };
    return colorMap[priority] || theme.palette.text.secondary;
  };

  if (accessDenied) return <AccessDenied />;

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      {/* Controls Section */}
      <Box sx={{ mb: 4, mt: { xs: 4, sm: 0 } }}>
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12, sm: 12, md: 3 }} >
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<Add />} 
              fullWidth
              onClick={() => {
                setEditingTask(null);
                setNewTask({ title: '', description: '', priority: 'Low', status: 'To Do', assignee: '', duedate: '', notes: '' });
                setOpenTaskDialog(true);
              }}
              sx={{ 
                height: '56px',
                borderRadius: 2,
                fontSize: { xs: '0.9rem', sm: '1rem' },
                fontWeight: 600,
                '&:hover': {
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              Add New Task
            </Button>
          </Grid2>

          {/* Filter Section */}
          <Grid2 size={{ xs: 12, sm: 4, md: 3 }} >
            <FormControl size="medium" fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                label="Priority"
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">All Priorities</MenuItem>
                {getUniqueFilterValues('priority').map((priority) => (
                  <MenuItem key={priority} value={priority}>{priority}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid2>
          
          <Grid2 size={{ xs: 12, sm: 4, md: 3 }} >
            <FormControl size="medium" fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">All Statuses</MenuItem>
                {getUniqueFilterValues('status').map((status) => (
                  <MenuItem key={status} value={status}>{status}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid2>
          
          <Grid2 size={{ xs: 12, sm: 4, md: 3 }} >
            <FormControl size="medium" fullWidth>
              <InputLabel>Assignee</InputLabel>
              <Select
                value={assigneeFilter}
                onChange={(e) => setAssigneeFilter(e.target.value)}
                label="Assignee"
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">All Assignees</MenuItem>
                {getUniqueFilterValues('assignee').map((assignee) => (
                  <MenuItem key={assignee} value={assignee}>{assignee}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid2>
        </Grid2>
      </Box>

      {/* Task List */}
      <Box sx={cardGridSx()}>
        {filteredTasks.length === 0 ? (
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Card sx={{ 
              p: { xs: 4, sm: 6 }, 
              textAlign: 'center',
              background: theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              boxShadow: theme.palette.mode === 'dark'
                ? '0 4px 20px rgba(0, 0, 0, 0.2)'
                : '0 4px 20px rgba(0, 0, 0, 0.1)',
              border: theme.palette.mode === 'dark'
                ? '1px solid rgba(255, 255, 255, 0.1)'
                : '1px solid rgba(255, 255, 255, 0.2)',
            }}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                gap: 2
              }}>
                <Assignment sx={{ 
                  fontSize: 64, 
                  color: theme.palette.primary.main,
                  opacity: 0.7
                }} />
                <Box>
                  <Typography variant="h5" sx={{ 
                    fontWeight: 600, 
                    color: theme.palette.text.primary,
                    mb: 1,
                    fontSize: { xs: '1.25rem', sm: '1.5rem' }
                  }}>
                    {(priorityFilter || statusFilter || assigneeFilter) ? 
                      'No tasks found for the selected filters' : 
                      'No tasks added yet'
                    }
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ 
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    lineHeight: 1.5
                  }}>
                    {(priorityFilter || statusFilter || assigneeFilter) ? 
                      'Try adjusting your filters to see more results.' : 
                      'Start organizing your event by clicking "Add New Task" above.'
                    }
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Box>
        ) : filteredTasks.map((task) => {
          const displayAssignee = !task.assignee || task.assignee.trim() === ''
            ? 'Unassigned'
            : capitalizeName(task.assignee);

          return (
            <Box key={task.id} sx={cardItemSx()}>
              <Card sx={{
                height: '100%',
                borderRadius: 3,
                background: theme.palette.mode === 'dark' 
                  ? 'rgba(255, 255, 255, 0.05)'
                  : 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 4px 20px rgba(0, 0, 0, 0.2)'
                  : '0 4px 20px rgba(0, 0, 0, 0.1)',
                border: getCardBorderColor(task.status),
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 8px 30px rgba(0, 0, 0, 0.3)'
                    : '0 8px 30px rgba(0, 0, 0, 0.15)',
                }
              }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 }, display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <Tooltip 
                    title={task.title} 
                    arrow 
                    placement="top"
                    sx={{ 
                      '& .MuiTooltip-tooltip': {
                        maxWidth: 300,
                        fontSize: '0.875rem',
                        lineHeight: 1.4
                      }
                    }}
                  >
                    <Typography variant="h6" sx={{ 
                      fontWeight: 600, 
                      mb: 2, 
                      minHeight: '2rem', 
                      height: '1.5rem', 
                      overflow: 'hidden', 
                      display: '-webkit-box', 
                      WebkitLineClamp: 1, 
                      WebkitBoxOrient: 'vertical',
                      cursor: 'help'
                    }}>
                      {task.title}
                    </Typography>
                  </Tooltip>
                  
                  {/* Description with conditional tooltip */}
                  <Box sx={{ mb: 2, position: 'relative' }}>
                    {(() => {
                      const description = task.description || 'No description provided';
                      const isTruncated = description.length > 100;
                      const isExpanded = expandedTexts[`${task.id}-description`];
                      
                      return (
                        <>
                          <Typography 
                            variant="body2" 
                            color="text.secondary" 
                            sx={{ 
                              fontSize: { xs: '0.75rem', sm: '0.8rem' },
                              lineHeight: 1.4,
                              overflow: 'hidden',
                              display: '-webkit-box',
                              WebkitLineClamp: isExpanded ? 'unset' : 3,
                              WebkitBoxOrient: 'vertical',
                              wordBreak: 'break-word',
                              height: isExpanded ? 'auto' : '3.4rem',
                              cursor: isTruncated ? 'pointer' : 'default',
                              '&:hover': isTruncated ? {
                                backgroundColor: theme.palette.action.hover,
                                borderRadius: 1
                              } : {}
                            }}
                          >
                            {description}
                          </Typography>
                          
                          {isTruncated && (
                            <>
                              <Tooltip 
                                title={description} 
                                arrow 
                                placement="top"
                                sx={{ 
                                  display: { xs: 'none', md: 'block' },
                                  '& .MuiTooltip-tooltip': {
                                    maxWidth: 300,
                                    fontSize: '0.875rem',
                                    lineHeight: 1.4
                                  }
                                }}
                              >
                                <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }} />
                              </Tooltip>
                              
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleTextExpand(task.id, 'description');
                                }}
                                sx={{
                                  position: 'absolute',
                                  bottom: -8,
                                  right: -8,
                                  backgroundColor: theme.palette.primary.main,
                                  color: theme.palette.primary.contrastText,
                                  width: 24,
                                  height: 24,
                                  '&:hover': {
                                    backgroundColor: theme.palette.primary.dark,
                                  },
                                  display: { xs: 'flex', md: 'none' },
                                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                  transition: 'transform 0.2s ease',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                }}
                              >
                                <ExpandMore sx={{ fontSize: 16 }} />
                              </IconButton>
                            </>
                          )}
                        </>
                      );
                    })()}
                  </Box>

                  {/* Task Details Section */}
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Flag sx={{ fontSize: { xs: 14, sm: 16 }, mr: 1, color: getPriorityColor(task.priority) }} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>
                        Priority: {task.priority}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Assignment sx={{ fontSize: { xs: 14, sm: 16 }, mr: 1, color: theme.palette.primary.main }} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>
                        Status: {task.status}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Person sx={{ fontSize: { xs: 14, sm: 16 }, mr: 1, color: theme.palette.primary.main }} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>
                        Assignee: {displayAssignee}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Schedule sx={{ fontSize: { xs: 14, sm: 16 }, mr: 1, color: theme.palette.primary.main }} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>
                        Due: {task.duedate || 'No due date'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Notes Section - Fixed height */}
                  <Box sx={{ mb: 2, minHeight: '2.5rem' }}>
                    {task.notes && (() => {
                      const notes = task.notes;
                      const isTruncated = notes.length > 100;
                      const isExpanded = expandedTexts[`${task.id}-notes`];
                      
                      return (
                        <Box sx={{ position: 'relative' }}>
                          <Typography 
                            variant="body2" 
                            color="text.secondary" 
                            sx={{ 
                              fontSize: { xs: '0.75rem', sm: '0.8rem' }, 
                              fontStyle: 'italic',
                              lineHeight: 1.4,
                              overflow: 'hidden',
                              display: '-webkit-box',
                              WebkitLineClamp: isExpanded ? 'unset' : 3,
                              WebkitBoxOrient: 'vertical',
                              wordBreak: 'break-word',
                              height: isExpanded ? 'auto' : '3.4rem',
                              cursor: isTruncated ? 'pointer' : 'default',
                              '&:hover': isTruncated ? {
                                backgroundColor: theme.palette.action.hover,
                                borderRadius: 1
                              } : {}
                            }}
                          >
                            Notes: {notes}
                          </Typography>
                          
                          {isTruncated && (
                            <>
                              <Tooltip 
                                title={notes} 
                                arrow 
                                placement="top"
                                sx={{ 
                                  display: { xs: 'none', md: 'block' },
                                  '& .MuiTooltip-tooltip': {
                                    maxWidth: 300,
                                    fontSize: '0.875rem',
                                    lineHeight: 1.4
                                  }
                                }}
                              >
                                <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }} />
                              </Tooltip>
                              
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleTextExpand(task.id, 'notes');
                                }}
                                sx={{
                                  position: 'absolute',
                                  bottom: -8,
                                  right: -8,
                                  backgroundColor: theme.palette.primary.main,
                                  color: theme.palette.primary.contrastText,
                                  width: 24,
                                  height: 24,
                                  '&:hover': {
                                    backgroundColor: theme.palette.primary.dark,
                                  },
                                  display: { xs: 'flex', md: 'none' },
                                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                  transition: 'transform 0.2s ease',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                }}
                              >
                                <ExpandMore sx={{ fontSize: 16 }} />
                              </IconButton>
                            </>
                          )}
                        </Box>
                      );
                    })()}
                  </Box>

                  {/* Progress Section - Always at bottom */}
                  <Box sx={{ mt: 'auto' }}>
                    <Box sx={{ mb: 2 }}>
                      <LinearProgress
                        variant="determinate"
                        value={calculateProgress(task.status)}
                        sx={{ 
                          height: 6,
                          borderRadius: 3,
                          bgcolor: theme.palette.grey[200],
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: getProgressBarColor(task.status),
                            borderRadius: 3
                          }
                        }}
                      />
                    </Box>

                    <Typography variant="body2" sx={{ 
                      fontWeight: 500,
                      color: task.status === 'Done' ? theme.palette.success.main : 
                             ['Cancelled', 'Blocked'].includes(task.status) ? theme.palette.error.main :
                             task.status === 'On Hold' ? theme.palette.warning.main : 
                             theme.palette.text.secondary,
                      mb: 2
                    }}>
                      {calculateProgress(task.status)}% Complete
                    </Typography>

                    {/* Action buttons */}
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      pt: 2,
                      borderTop: `1px solid ${theme.palette.divider}`,
                      gap: 0.5
                    }}>
                      <Tooltip title="Edit Task" arrow>
                        <IconButton 
                          size="small"
                          onClick={() => handleEdit(task)}
                          sx={{ 
                            color: theme.palette.primary.main,
                            '&:hover': { backgroundColor: theme.palette.primary.main + '20' }
                          }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Task" arrow>
                        <IconButton 
                          size="small"
                          onClick={() => handleDeleteOpen(task.id)}
                          sx={{ 
                            color: theme.palette.error.main,
                            '&:hover': { backgroundColor: theme.palette.error.main + '20' }
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={task.status === 'Done' ? 'Task Completed' : 'Mark as complete'} arrow>
                        <IconButton 
                          size="small"
                          onClick={() => handleComplete(task.id)}
                          sx={{ 
                            color: task.status === 'Done' ? theme.palette.success.main : theme.palette.info.main,
                            '&:hover': { backgroundColor: (task.status === 'Done' ? theme.palette.success.main : theme.palette.info.main) + '20' }
                          }}
                        >
                          {task.status === 'Done' ? <CheckCircle fontSize="small" /> : <Done fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          );
        })}
      </Box>

      {/* Add/Edit Task Dialog */}
      <Dialog open={openTaskDialog} onClose={() => setOpenTaskDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ 
          fontWeight: 600,
          background: theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(255, 255, 255, 0.9)',
        }}>
          {editingTask ? 'Edit Task' : 'Add New Task'}
        </DialogTitle>
        <DialogContent sx={{ 
          background: theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.02)'
            : 'rgba(255, 255, 255, 0.9)',
          pt: 2
        }}>
          <Grid2 container spacing={2}>
            <Grid2 size={12}>
              <TextField 
                label="Title" 
                name="title" 
                value={newTask.title} 
                onChange={handleInputChange} 
                fullWidth 
                margin="normal"
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid2>
            <Grid2 size={12}>
              <TextField 
                label="Description" 
                name="description" 
                value={newTask.description} 
                onChange={handleInputChange} 
                fullWidth 
                margin="normal"
                multiline
                rows={3}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid2>
            <Grid2 size={6}>
              <TextField 
                select 
                label="Priority" 
                name="priority" 
                value={newTask.priority} 
                onChange={handleInputChange} 
                fullWidth 
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              >
                <MenuItem value="High">High</MenuItem>
                <MenuItem value="Medium">Medium</MenuItem>
                <MenuItem value="Low">Low</MenuItem>
              </TextField>
            </Grid2>
            <Grid2 size={6}>
              <TextField 
                select 
                label="Status" 
                name="status" 
                value={newTask.status} 
                onChange={handleInputChange} 
                fullWidth 
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              >
                <MenuItem value="To Do">To Do</MenuItem>
                <MenuItem value="In Progress">In Progress</MenuItem>
                <MenuItem value="On Hold">On Hold</MenuItem>
                <MenuItem value="Cancelled">Cancelled</MenuItem>
                <MenuItem value="Pending Review">Pending Review</MenuItem>
                <MenuItem value="Scheduled">Scheduled</MenuItem>
                <MenuItem value="Blocked">Blocked</MenuItem>
                <MenuItem value="Done">Done</MenuItem>
              </TextField>
            </Grid2>
            <Grid2 size={6}>
              <TextField 
                label="Assignee" 
                name="assignee" 
                value={newTask.assignee} 
                onChange={handleInputChange} 
                fullWidth 
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid2>
            <Grid2 size={6}>
              <TextField 
                label="Due Date" 
                name="duedate" 
                value={newTask.duedate} 
                onChange={handleInputChange} 
                fullWidth 
                margin="normal" 
                type="date" 
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid2>
            <Grid2 size={12}>
              <TextField 
                label="Notes" 
                name="notes" 
                value={newTask.notes} 
                onChange={handleInputChange} 
                fullWidth 
                margin="normal" 
                multiline 
                rows={4}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid2>
          </Grid2>
        </DialogContent>
        <DialogActions sx={{ 
          background: theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(255, 255, 255, 0.9)',
          p: { xs: 1, sm: 2 },
          gap: { xs: 1, sm: 0 },
          flexDirection: 'row',
          alignItems: 'center'
        }}>
          <Button 
            onClick={() => setOpenTaskDialog(false)} 
            color="error"
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddOrEditTask} 
            color="primary"
            variant="contained"
            sx={{ borderRadius: 2 }}
          >
            {editingTask ? 'Save' : 'Add Task'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog for Deleting Task */}
      <Dialog open={openDeleteDialog} onClose={handleDeleteCancel} maxWidth="sm">
        <DialogTitle sx={{ 
          fontWeight: 600,
          background: theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(255, 255, 255, 0.9)',
        }}>
          Confirm Deletion
        </DialogTitle>
        <DialogContent sx={{ 
          background: theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.02)'
            : 'rgba(255, 255, 255, 0.9)',
          pt: 2
        }}>
          <Typography sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
            Are you sure you want to delete this task? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ 
          background: theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(255, 255, 255, 0.9)',
          p: { xs: 1, sm: 2 },
          gap: { xs: 1, sm: 0 },
          flexDirection: 'row',
          alignItems: 'center'
        }}>
          <Button 
            onClick={handleDeleteCancel} 
            color="primary"
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error"
            variant="contained"
            sx={{ borderRadius: 2 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TaskManagement;
